import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type { Customer, Device, DeviceLifecycleState, Loan, LoanStatus } from "@dfp/api-client";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CreateDeviceForm } from "../components/CreateDeviceForm.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";
import { formatMoney } from "../lib/utils.js";

/** Loan-status → badge tone, for the device-list pivot column. */
const LOAN_STATUS_TONE: Record<LoanStatus, BadgeTone> = {
  DRAFT: "neutral",
  PENDING_VERIFICATION: "warning",
  PENDING_DEPOSIT: "warning",
  PENDING_DEVICE_PROVISIONING: "warning",
  ACTIVE: "success",
  IN_ARREARS: "warning",
  LOCKED: "danger",
  IN_DISPUTE: "warning",
  PENDING_RELEASE: "brand",
  PAID_OFF: "neutral",
  WRITTEN_OFF: "neutral",
  REJECTED: "neutral",
};

/** A loan is "live" (still financing the device) unless fully closed. */
const TERMINAL_LOAN_STATUSES: ReadonlySet<LoanStatus> = new Set<LoanStatus>([
  "PAID_OFF",
  "WRITTEN_OFF",
]);

/**
 * A device can carry multiple loans over its lifetime (re-financing).
 * Pick the one that best represents "what's this device doing now":
 * a still-live loan wins over a closed one; ties break on most-recent.
 */
function pickRepresentativeLoan(loans: readonly Loan[]): Loan | undefined {
  if (loans.length === 0) return undefined;
  const live = loans.filter((l) => !TERMINAL_LOAN_STATUSES.has(l.status));
  const pool = live.length > 0 ? live : loans;
  return [...pool].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

const STATE_TONE: Record<DeviceLifecycleState, BadgeTone> = {
  PROCURED: "neutral",
  WAREHOUSE_RECEIVED: "neutral",
  DEVICE_REGISTERED: "neutral",
  CONTROL_ENROLLED: "brand",
  ASSIGNED_TO_DEALER: "brand",
  RESERVED_FOR_CUSTOMER: "brand",
  HANDED_OVER: "brand",
  ACTIVE_FINANCED: "success",
  PENDING_RELEASE: "brand",
  PAID_OFF: "success",
  RELEASED: "success",
  RETURNED: "neutral",
  REPOSSESSING: "warning",
  REPOSSESSED: "warning",
  REFURBISHING: "warning",
  READY_FOR_RESALE: "brand",
  DAMAGED: "warning",
  LOST: "danger",
  STOLEN: "danger",
  WRITTEN_OFF: "danger",
  RESOLD: "neutral",
};

export function DevicesPage(): JSX.Element {
  // Drill-down filters from the Fleet page (version / not-reported / provider).
  const search = useSearch({ strict: false }) as {
    versionCode?: string;
    notReported?: string;
    provider?: string;
  };
  const devices = useQuery({
    queryKey: ["devices", "list"],
    queryFn: () => api.get<readonly Device[]>("/api/devices"),
  });
  // Side-fetches for the device → loan → customer pivot. Same bounded
  // client-side join LoansPage uses; move to a server include endpoint
  // when list sizes outgrow it.
  const loans = useQuery({
    queryKey: ["loans", "list"],
    queryFn: () => api.get<readonly Loan[]>("/api/loans"),
  });
  const customers = useQuery({
    queryKey: ["customers", "list"],
    queryFn: () => api.get<readonly Customer[]>("/api/customers"),
  });

  // deviceId → representative loan, and customerId → customer.
  const loanByDeviceId = useMemo(() => {
    const byDevice = new Map<string, Loan[]>();
    for (const l of loans.data ?? []) {
      const arr = byDevice.get(l.deviceId);
      if (arr) arr.push(l);
      else byDevice.set(l.deviceId, [l]);
    }
    const out = new Map<string, Loan>();
    for (const [deviceId, list] of byDevice) {
      const pick = pickRepresentativeLoan(list);
      if (pick) out.set(deviceId, pick);
    }
    return out;
  }, [loans.data]);
  const customerById = useMemo(
    () => new Map((customers.data ?? []).map((c) => [c.id, c])),
    [customers.data],
  );

  // Apply the Fleet drill-down filters client-side (the list is one country's
  // worth of devices; a server filter is a follow-up if it outgrows this).
  const versionFilter =
    search.versionCode != null && search.versionCode !== "" ? Number(search.versionCode) : null;
  const filtered = useMemo(() => {
    let list = devices.data ?? [];
    if (versionFilter != null && !Number.isNaN(versionFilter)) {
      list = list.filter((d) => d.dpcVersionCode === versionFilter);
    }
    if (search.notReported === "1") {
      list = list.filter((d) => d.dpcVersionCode == null && d.dpcVersionName == null);
    }
    if (search.provider) {
      list = list.filter((d) => d.controlProvider === search.provider);
    }
    return list;
  }, [devices.data, versionFilter, search.notReported, search.provider]);

  const activeFilter =
    versionFilter != null
      ? `DPC version code ${search.versionCode}`
      : search.notReported === "1"
        ? "DPC version: not reported"
        : search.provider
          ? `control provider: ${search.provider}`
          : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Devices</h1>
        <p className="text-sm text-slate-500">All devices and their lifecycle state.</p>
        {activeFilter ? (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs text-brand-700">
            <span>
              Filtered to <strong>{activeFilter}</strong>
            </span>
            <Link to="/devices" className="font-semibold text-brand-700 hover:underline">
              clear ✕
            </Link>
          </div>
        ) : null}
      </div>

      <CreateDeviceForm />

      <Card>
        <CardHeader>
          <CardTitle>All devices</CardTitle>
          <span className="text-xs text-slate-500">
            {filtered.length}
            {activeFilter ? ` of ${devices.data?.length ?? 0}` : ""} rows
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {devices.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : devices.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load devices.</p>
          ) : filtered.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">
              {activeFilter ? "No devices match this filter." : "No devices yet."}
            </p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>IMEI</Th>
                  <Th>Brand · Model</Th>
                  <Th>Value</Th>
                  <Th>Risk</Th>
                  <Th>State</Th>
                  <Th>Loan</Th>
                  <Th>Customer</Th>
                </tr>
              </THead>
              <TBody>
                {filtered.map((d) => (
                  <Tr key={d.id}>
                    <Td>
                      <Link
                        to="/devices/$id"
                        params={{ id: d.id }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {d.id.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td className="font-mono text-xs">{d.imei}</Td>
                    <Td>
                      {d.brand} {d.model}
                    </Td>
                    <Td>{formatMoney(d.valueAmountMinor, d.valueCurrency)}</Td>
                    <Td>
                      <Badge
                        tone={
                          d.modelRiskClass === "A"
                            ? "success"
                            : d.modelRiskClass === "B"
                              ? "neutral"
                              : "warning"
                        }
                      >
                        {d.modelRiskClass}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={STATE_TONE[d.lifecycleState]}>{d.lifecycleState}</Badge>
                    </Td>
                    <Td>
                      {(() => {
                        const loan = loanByDeviceId.get(d.id);
                        if (!loan) {
                          return <span className="text-xs text-slate-400">—</span>;
                        }
                        return (
                          <Link
                            to="/loans/$id"
                            params={{ id: loan.id }}
                            className="hover:underline"
                          >
                            <Badge tone={LOAN_STATUS_TONE[loan.status]}>{loan.status}</Badge>
                          </Link>
                        );
                      })()}
                    </Td>
                    <Td>
                      {(() => {
                        const loan = loanByDeviceId.get(d.id);
                        const customer = loan ? customerById.get(loan.customerId) : undefined;
                        if (!loan) return <span className="text-xs text-slate-400">—</span>;
                        return (
                          <Link
                            to="/customers/$id"
                            params={{ id: loan.customerId }}
                            className="text-sm text-brand-600 hover:underline"
                          >
                            {customer
                              ? `${customer.givenName} ${customer.familyName}`
                              : `${loan.customerId.slice(0, 8)}…`}
                          </Link>
                        );
                      })()}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
